import { useRef } from "react";
import {
  Briefcase, Building2, MapPin, Users2, Landmark,
  MoreHorizontal, Mail, Phone, Copy, ExternalLink, Camera, Trash2, ImagePlus,
  CalendarDays, Hash, Clock,
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EmployeeProfile } from "@/data/employees";
import { calcTenure, formatStartDate } from "@/data/employees";
import { apiUploadAvatar, apiDeleteAvatar, apiGetCompanyName } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface ProfileHeaderProps {
  employee: EmployeeProfile;
  isOwnProfile?: boolean;
  /** Extra controls rendered in the top-right, just below the email/call buttons.
   *  Used by the Organisation → Employees popup for the role + activate/deactivate controls. */
  headerExtra?: React.ReactNode;
}

export function ProfileHeader({ employee, isOwnProfile = false, headerExtra }: ProfileHeaderProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const tenure    = calcTenure(employee.startDate);
  const startFmt  = formatStartDate(employee.startDate);
  const isAdmin   = user?.role.mainRole === "admin";
  const isHr      = user?.role.mainRole === "hr";
  const canManageAvatar = isOwnProfile || isAdmin || isHr;
  const accentGradient  = employee.avatarColor ?? "from-violet-500 to-indigo-500";

  // ── Mutations ──────────────────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: (file: File) => apiUploadAvatar(employee.id, file),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["employee", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee-by-email"] });
      if (isOwnProfile && data?.avatarUrl) updateUser({ avatarUrl: data.avatarUrl });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
    },
    onError: (err: Error) => {
      toast({ title: t("profile.editError"), description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDeleteAvatar(employee.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employee", employee.id] });
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      queryClient.invalidateQueries({ queryKey: ["employee-by-email"] });
      if (isOwnProfile) updateUser({ avatarUrl: null });
      toast({ title: t("profile.editSuccess"), description: t("profile.editSuccessDesc") });
    },
    onError: () => {
      toast({ title: t("profile.editError"), description: t("profile.editErrorDesc"), variant: "destructive" });
    },
  });

  const { data: companyName } = useQuery({
    queryKey: ["company-name"],
    queryFn: apiGetCompanyName,
    staleTime: 5 * 60 * 1000,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = "";
  };

  const copyToClipboard = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: t("common.copied"), description: label ? `${label}: ${text}` : undefined });
  };

  const metaTags = [
    employee.department  && { icon: Building2, label: employee.department },
    employee.location    && { icon: MapPin,     label: employee.location },
    employee.team        && { icon: Users2,      label: employee.team },
    (companyName || employee.company) && { icon: Landmark, label: companyName || employee.company },
    employee.employeeId  && { icon: Hash,        label: employee.employeeId },
  ].filter(Boolean) as { icon: React.ElementType; label: string }[];

  // ── Avatar with optional upload trigger ───────────────────────────────────
  const AvatarEl = (
    <div className="relative group">
      {/* Accent ring that picks up the employee's personal colour */}
      <div className={`p-0.5 rounded-full bg-gradient-to-br ${accentGradient}`}>
        <div className="ring-2 ring-card rounded-full">
          <EmployeeAvatar
            avatarUrl={employee.avatarUrl}
            avatarColor={employee.avatarColor}
            initials={employee.initials}
            name={employee.name}
            size="xl"
          />
        </div>
      </div>
      {canManageAvatar && (
        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
          <Camera className="h-5 w-5 text-white" />
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-2xl overflow-hidden border border-border/60 shadow-sm bg-card">

      {/* ── 4 px personalised colour accent strip ─────────────────────────── */}
      <div className={`h-1 bg-gradient-to-r ${accentGradient}`} />

      {/* ── Main identity row ─────────────────────────────────────────────── */}
      <div className="px-5 sm:px-6 pt-5 pb-4">
        <div className="flex items-start gap-4 sm:gap-5">

          {/* Avatar — clickable if allowed to manage */}
          {canManageAvatar ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="shrink-0 focus:outline-none">{AvatarEl}</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                  <ImagePlus className="h-4 w-4 mr-2" />
                  {employee.avatarUrl ? t("profile.actions.changePhoto") : t("profile.actions.uploadPhoto")}
                </DropdownMenuItem>
                {employee.avatarUrl && (
                  <DropdownMenuItem
                    onClick={() => deleteMutation.mutate()}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t("profile.actions.removePhoto")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="shrink-0">{AvatarEl}</div>
          )}

          {/* Name + title + status */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
                {employee.name}
              </h1>
              {employee.accessMode === "limited" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400 cursor-help">
                      {t("leave.accessMode.limited")}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t("leave.accessMode.limitedSubmitBanner")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
              {employee.jobTitle}
            </p>
          </div>

          {/* ── Quick-action buttons — always one click away ──────────────── */}
          <div className="shrink-0 flex flex-col items-end gap-2 pt-0.5">
          <div className="flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={`mailto:${employee.email}`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-muted hover:bg-muted/80 border border-border/60 text-xs font-medium text-foreground/80 hover:text-foreground transition-all"
                >
                  <Mail className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Email</span>
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom">{employee.email}</TooltipContent>
            </Tooltip>

            {employee.phone && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href={`tel:${employee.phone}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-muted hover:bg-muted/80 border border-border/60 text-xs font-medium text-foreground/80 hover:text-foreground transition-all"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Call</span>
                  </a>
                </TooltipTrigger>
                <TooltipContent side="bottom">{employee.phone}</TooltipContent>
              </Tooltip>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="h-8 w-8 rounded-lg bg-muted hover:bg-muted/80 border border-border/60 flex items-center justify-center text-foreground/70 hover:text-foreground transition-all">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => copyToClipboard(employee.email, t("common.email"))}>
                  <Copy className="h-4 w-4 mr-2" />{t("profile.actions.copyEmail")}
                </DropdownMenuItem>
                {employee.phone && (
                  <DropdownMenuItem onClick={() => copyToClipboard(employee.phone, t("common.call"))}>
                    <Copy className="h-4 w-4 mr-2" />{t("profile.actions.copyPhone")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href={`mailto:${employee.email}`}>
                    <Mail className="h-4 w-4 mr-2" />{t("profile.actions.sendEmail")}
                  </a>
                </DropdownMenuItem>
                {employee.phone && (
                  <DropdownMenuItem asChild>
                    <a href={`tel:${employee.phone}`}>
                      <Phone className="h-4 w-4 mr-2" />{t("profile.actions.callPhone")}
                    </a>
                  </DropdownMenuItem>
                )}
                {!isOwnProfile && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to={`/employees/${employee.id}`}>
                        <ExternalLink className="h-4 w-4 mr-2" />{t("profile.actions.viewFullProfile")}
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {headerExtra}
          </div>
        </div>

        {/* ── Meta chips — department / location / team / company / ID ─────── */}
        {metaTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-4 ml-[72px] sm:ml-[80px]">
            {metaTags.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted/60 border border-border/40 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <Icon className="h-3 w-3 shrink-0" />
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer info bar — tenure + reports-to ─────────────────────────── */}
      <div className="px-5 sm:px-6 py-3 border-t border-border/50 bg-muted/30 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5 shrink-0" />
          {t("profile.since")} {startFmt}
        </span>
        <span className="text-border/60 select-none text-xs">·</span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3 shrink-0" />
          <span className="font-medium text-foreground">{tenure}</span>
        </span>

        {employee.reportsTo && (
          <>
            <span className="text-border/60 select-none text-xs">·</span>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {t("profile.reportsTo")}
              <Link
                to={`/employees/${employee.reportsTo.id}`}
                className="inline-flex items-center gap-1.5 font-medium text-foreground hover:text-primary transition-colors group"
              >
                <EmployeeAvatar
                  avatarUrl={employee.reportsTo.avatarUrl}
                  avatarColor={employee.reportsTo.avatarColor}
                  initials={employee.reportsTo.initials}
                  name={employee.reportsTo.name}
                  size="xs"
                />
                <span className="group-hover:underline underline-offset-2">
                  {employee.reportsTo.name}
                </span>
              </Link>
            </span>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
